import { useCallback, useState } from 'react';
import { API } from '@/utils/API';
import { FormTreeData } from '@/features/submission_forms/types';

export interface SubmissionTemplateVersionData {
  id: string;
  submission_template_id: string;
  version_number: number;
  status: 'draft' | 'published' | 'archived';
  created_by: string | null;
  published_by: string | null;
  published_at: string | null;
  created_at: string;
  updated_at: string;
  // Set only when this version was created by "Revert to this version" —
  // the id of the archived version it copied from. Null for a version
  // reached through the normal edit-draft-then-publish path.
  reverted_from_version_id: string | null;
  question_tree?: FormTreeData[];
}

export interface ResetJobData {
  id: string;
  state: 'queued' | 'running' | 'finished' | 'failed';
  enqueued_at: string;
  performed_at: string | null;
  finished_at: string | null;
  error: string | null;
}

export interface ResetStatusData {
  production: boolean;
  configured: boolean;
  last_job: ResetJobData | null;
}

const parseErrorOrThrow = async (response: Response, fallback: string) => {
  if (response.ok) return;
  const body = await response.json().catch(() => null);
  throw new Error(body?.errors?.[0] || body?.error || fallback);
};

export const useSubmissionTemplateVersion = () => {
  const [isSaving, setIsSaving] = useState(false);

  const listVersions = useCallback(
    async (templateId: string): Promise<SubmissionTemplateVersionData[]> => {
      const response = await API.authenticated.admin.fetch(
        `/submission_templates/${templateId}/submission_template_versions`
      );
      await parseErrorOrThrow(response, 'Failed to load versions');
      return response.json();
    },
    []
  );

  // Read-only fetch of one version, including its question_tree.
  const getVersion = useCallback(
    async (
      templateId: string,
      versionId: string
    ): Promise<SubmissionTemplateVersionData> => {
      const response = await API.authenticated.admin.fetch(
        `/submission_templates/${templateId}/submission_template_versions/${versionId}`
      );
      await parseErrorOrThrow(response, 'Failed to load version');
      return response.json();
    },
    []
  );

  // Opens a draft if none exists yet, otherwise returns the existing one —
  // callers don't need to care which case they hit.
  const openOrLoadDraft = useCallback(
    async (templateId: string): Promise<SubmissionTemplateVersionData> => {
      const response = await API.authenticated.admin.fetch(
        `/submission_templates/${templateId}/submission_template_versions`,
        { method: 'POST' }
      );
      await parseErrorOrThrow(response, 'Failed to open a draft version');
      return response.json();
    },
    []
  );

  const saveDraftTree = useCallback(
    async (
      templateId: string,
      versionId: string,
      questionTree: FormTreeData[]
    ): Promise<SubmissionTemplateVersionData> => {
      setIsSaving(true);
      try {
        const response = await API.authenticated.admin.fetch(
          `/submission_templates/${templateId}/submission_template_versions/${versionId}`,
          {
            method: 'PATCH',
            body: JSON.stringify({
              submission_template_version: { question_tree: questionTree },
            }),
          }
        );
        await parseErrorOrThrow(response, 'Failed to save the draft');
        return response.json();
      } finally {
        setIsSaving(false);
      }
    },
    []
  );

  const publishVersion = useCallback(
    async (versionId: string): Promise<SubmissionTemplateVersionData> => {
      const response = await API.authenticated.admin.fetch(
        '/submission_template_versions/publishes',
        {
          method: 'POST',
          body: JSON.stringify({
            publish: { submission_template_version_id: versionId },
          }),
        }
      );
      await parseErrorOrThrow(response, 'Failed to publish');
      return response.json();
    },
    []
  );

  const discardDraft = useCallback(
    async (versionId: string): Promise<SubmissionTemplateVersionData> => {
      const response = await API.authenticated.admin.fetch(
        '/submission_template_versions/discards',
        {
          method: 'POST',
          body: JSON.stringify({
            discard: { submission_template_version_id: versionId },
          }),
        }
      );
      await parseErrorOrThrow(response, 'Failed to discard changes');
      return response.json();
    },
    []
  );

  const revertToVersion = useCallback(
    async (
      templateId: string,
      targetVersionId: string
    ): Promise<SubmissionTemplateVersionData> => {
      const response = await API.authenticated.admin.fetch(
        '/submission_template_versions/reverts',
        {
          method: 'POST',
          body: JSON.stringify({
            revert: {
              submission_template_id: templateId,
              target_version_id: targetVersionId,
            },
          }),
        }
      );
      await parseErrorOrThrow(response, 'Failed to revert');
      return response.json();
    },
    []
  );

  const compareVersions = useCallback(
    async (baseVersionId: string, targetVersionId: string) => {
      const response = await API.authenticated.admin.fetch(
        `/submission_template_versions/comparison?base_version_id=${baseVersionId}&target_version_id=${targetVersionId}`
      );
      await parseErrorOrThrow(response, 'Failed to compare versions');
      return response.json();
    },
    []
  );

  const getResetStatus = useCallback(
    async (templateId: string): Promise<ResetStatusData> => {
      const response = await API.authenticated.admin.fetch(
        `/submission_template_versions/reset?submission_template_id=${templateId}`
      );
      await parseErrorOrThrow(response, 'Failed to load reset status');
      return response.json();
    },
    []
  );

  const triggerReset = useCallback(
    async (templateId: string): Promise<{ last_job: ResetJobData | null }> => {
      const response = await API.authenticated.admin.fetch(
        '/submission_template_versions/reset',
        {
          method: 'POST',
          body: JSON.stringify({
            reset: { submission_template_id: templateId },
          }),
        }
      );
      await parseErrorOrThrow(response, 'Failed to start reset to prod');
      return response.json();
    },
    []
  );

  const getResetAllStatus = useCallback(async (): Promise<ResetStatusData> => {
    const response = await API.authenticated.admin.fetch(
      '/submission_template_versions/reset_all'
    );
    await parseErrorOrThrow(response, 'Failed to load reset-all status');
    return response.json();
  }, []);

  const triggerResetAll = useCallback(async (): Promise<{
    last_job: ResetJobData | null;
  }> => {
    const response = await API.authenticated.admin.fetch(
      '/submission_template_versions/reset_all',
      { method: 'POST' }
    );
    await parseErrorOrThrow(response, 'Failed to start reset all to prod');
    return response.json();
  }, []);

  return {
    isSaving,
    listVersions,
    getVersion,
    openOrLoadDraft,
    saveDraftTree,
    publishVersion,
    discardDraft,
    revertToVersion,
    compareVersions,
    getResetStatus,
    triggerReset,
    getResetAllStatus,
    triggerResetAll,
  };
};
